import { NodeViewSettingsPanel, type NodeViewSettingsPanelField } from '@/components/common/node-view-settings-panel';
import { CommonNodeViewWrapper } from '@/components/common/node-view-wrapper';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useIsEditorReadonly } from '@/hooks/use-is-editor-readonly';
import { cn } from '@/lib/utils';
import type { ReactNodeViewProps } from '@tiptap/react';
import { NodeViewContent } from '@tiptap/react';
import { Plus } from 'lucide-react';
import { memo, useCallback, useMemo, useState } from 'react';
import { ITLAB_TAB, ITLAB_TAB_LIST, hasZyITLabOptionalTask, type ITLabTab } from './zyitlab-shared';
import { ZyITLabVmPreview } from './zyitlab-vm-preview';

const ZyITLabNodeView: React.FC<ReactNodeViewProps> = ({ node, updateAttributes, selected, editor, decorations, getPos }) => {
    const isEditorReadonly = useIsEditorReadonly(editor);
    const caption = node.attrs.caption || '';
    const [activeTab, setActiveTab] = useState<ITLabTab>(ITLAB_TAB.INSTRUCTIONS);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const handleFullscreenChange = useCallback((fullscreen: boolean) => {
        setIsFullscreen(fullscreen);
        if (fullscreen) {
            setActiveTab(tab => (tab === ITLAB_TAB.VM_PREVIEW ? ITLAB_TAB.INSTRUCTIONS : tab));
        }
    }, []);

    const handleCaptionChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            updateAttributes({ caption: e.target.value });
        },
        [updateAttributes],
    );

    const hasOptionalTask = hasZyITLabOptionalTask(node);

    const settingsPanelFields = useMemo(
        () =>
            [
                {
                    label: 'Caption',
                    type: 'text',
                    value: caption,
                    onChange: handleCaptionChange,
                    placeholder: 'Lab title...',
                    searchAttr: 'caption',
                },
            ] as NodeViewSettingsPanelField[],
        [caption, handleCaptionChange],
    );

    const showVmPreviewInTab = !isFullscreen && activeTab === ITLAB_TAB.VM_PREVIEW;

    const vmPreview = (
        <div
            className={cn(
                isFullscreen ? 'w-1/2 min-w-0 overflow-hidden border-l border-gray-200 dark:border-gray-700' : '',
                !isFullscreen && !showVmPreviewInTab && 'hidden',
            )}
            contentEditable={false}
        >
            <ZyITLabVmPreview
                editor={editor}
                node={node}
                getPos={getPos}
                fillHeight={isFullscreen}
            />
        </div>
    );

    const mainContent = (
        <div className={cn(isFullscreen ? 'flex h-full min-h-0' : 'space-y-4')}>
            <div
                className={cn('zyitlab-tab-sections', isFullscreen ? 'w-1/2 min-w-0 overflow-y-auto px-4 py-4' : 'space-y-4')}
                data-itlab-tab={activeTab}
            >
                <NodeViewContent />

                {activeTab === ITLAB_TAB.INSTRUCTIONS && !hasOptionalTask && (
                    <div
                        className="pt-2"
                        contentEditable={false}
                    >
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={isEditorReadonly}
                            onClick={() => editor.commands.addZyITLabOptionalTask(getPos())}
                        >
                            <Plus className="mr-2 h-4 w-4" />
                            Add optional task
                        </Button>
                    </div>
                )}

                {activeTab === ITLAB_TAB.TASKS && (
                    <div
                        className="pt-2"
                        contentEditable={false}
                    >
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={isEditorReadonly}
                            onClick={() => editor.commands.addZyITLabTask(getPos())}
                        >
                            <Plus className="mr-2 h-4 w-4" />
                            Add task
                        </Button>
                    </div>
                )}
            </div>
            {vmPreview}
        </div>
    );

    return (
        <CommonNodeViewWrapper
            decorations={decorations}
            label="IT Lab"
            selected={selected}
            isFullscreen={isFullscreen}
            onFullscreenChange={handleFullscreenChange}
            settingsPanel={
                <NodeViewSettingsPanel fields={settingsPanelFields}>
                    <div
                        className="mt-4"
                        contentEditable={false}
                    >
                        <Tabs
                            value={activeTab}
                            onValueChange={value => setActiveTab(value as ITLabTab)}
                        >
                            <TabsList className="grid h-auto w-full grid-cols-2 gap-1 sm:grid-cols-2 lg:grid-cols-4">
                                {ITLAB_TAB_LIST.map(tab => (
                                    <TabsTrigger
                                        key={tab.value}
                                        value={tab.value}
                                        className="text-xs sm:text-sm"
                                        disabled={isFullscreen && tab.value === ITLAB_TAB.VM_PREVIEW}
                                    >
                                        {tab.label}
                                    </TabsTrigger>
                                ))}
                            </TabsList>
                        </Tabs>
                    </div>
                </NodeViewSettingsPanel>
            }
            contentClassName={cn('px-4 py-4', isFullscreen ? 'flex-1 min-h-0 overflow-hidden' : 'min-h-[600px] h-[600px] overflow-y-auto')}
            fullscreenContent={mainContent}
            fullscreenMode="inline"
            contentHeightClassName=""
            containerClassName="bg-white dark:bg-black"
        >
            {mainContent}
        </CommonNodeViewWrapper>
    );
};

export default memo(ZyITLabNodeView);
